import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { AssignableUser } from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import { useLiveQuery } from "@tanstack/react-db";
import { useMemo } from "react";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

/**
 * Every accessible support user across every connected team the caller
 * has access to. Backed by the global TanStack DB users collection —
 * fetches once, cached for the session, sliced per-ticket by the
 * assignee picker via `useUsersForAccount`.
 */
export function useAccessibleSupportUsers(): readonly AssignableUser[] {
  const collections = useGlobalSupportTicketCollections();
  const result = useLiveQuery(
    (q) => q.from({ user: collections.users }),
    [collections.users],
  );
  return result.data ?? [];
}

/**
 * `MongoUserId → AssignableUser` map for surfaces that receive a userId
 * over the wire (e.g. presence events) and need to render a name. Both
 * sides of the wire are typed `MongoUserId`, so the map is keyed by the
 * branded value directly — no `toString` conversion, no string
 * mismatches.
 */
export function useAccessibleSupportUserMap(): Map<
  MongoUserId,
  AssignableUser
> {
  const users = useAccessibleSupportUsers();
  return useMemo(() => {
    const map = new Map<MongoUserId, AssignableUser>();
    for (const user of users) {
      map.set(user._id, user);
    }
    return map;
  }, [users]);
}
